import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import { getSniperLink } from 'holocron/sniper-link';

import { EMAIL_SUBSCRIBE, EMAIL_ERROR, OPT_IN_REQUIRED } from 'Redux/ducks/email-subscribe';
import { toggleFeedbackPopup } from 'Redux/ducks/feedback-popups';
import newsletterMessages from 'Helpers/Messages/newsletter-messages';


const messages = defineMessages({
  successTitle: {
    id: 'email.subscribe.feedback.success.title',
    defaultMessage: 'Success!',
  },
  successMessage: {
    id: 'email.subscribe.feedback.success.message',
    defaultMessage: 'You are now subscribed to the Fanatical newsletter.',
  },
  errorTitle: {
    id: 'email.subscribe.feedback.error.title',
    defaultMessage: 'Something went wrong',
  },
  errorMessage: {
    id: 'email.subscribe.feedback.error.message',
    defaultMessage: 'Whoops something went wrong please try again.',
  },
});

/**
 * Raises a feedback popup for the result of an email subscribe posted from `context`, reflecting
 * the status the API replied with - subscribed gets the success copy, opt_in_required gets the
 * shared "confirm your subscription" copy and a sniper link, and no_change gets no popup because
 * the duck never leaves its initial state. The 24 hour suppression window is recorded by
 * postEmail, so it applies whether or not a placement uses this hook
 * @param {String} context subscribedFrom context this placement posts with
 */
const useEmailSubscribeFeedback = (context) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const {
    currentState,
    status,
    email,
    context: subscribedFrom,
  } = useSelector(state => state.emailSubscribe);

  const sniperLink = getSniperLink(email)?.url;

  useEffect(() => {
    if (subscribedFrom !== context) {
      return;
    }

    if (currentState === EMAIL_SUBSCRIBE) {
      if (status === OPT_IN_REQUIRED) {
        dispatch(toggleFeedbackPopup({
          type: 'info',
          display: 'persist',
          title: formatMessage(newsletterMessages.optinTitle),
          message: formatMessage(newsletterMessages.optinMessage),
          link: sniperLink
            ? { href: sniperLink, text: formatMessage(newsletterMessages.optinLink) }
            : null,
        }));
      } else {
        dispatch(toggleFeedbackPopup({
          type: 'success',
          title: formatMessage(messages.successTitle),
          message: formatMessage(messages.successMessage),
        }));
      }
    }

    if (currentState === EMAIL_ERROR) {
      dispatch(toggleFeedbackPopup({
        type: 'error',
        title: formatMessage(messages.errorTitle),
        message: formatMessage(messages.errorMessage),
      }));
    }
  }, [currentState, status, email, subscribedFrom, context, formatMessage, dispatch, sniperLink]);
};

export default useEmailSubscribeFeedback;
